import { attributes as mockAttributes, contentTypes as mockContentTypes, floors as mockFloors, genres as mockGenres } from "@/mocks/genres";
import { contentTypeConditionSets, services as mockServices } from "@/mocks/categoryConfig";
import {
  SITE_TODAY,
  getMakers,
  getPeople,
  getRelatedWorks as getRelatedWorksSync,
  getWorksByMode,
  matchesAudience,
  works,
} from "@/lib/catalog";
import { getIdIndex, getWorkByRefKey } from "@/lib/catalogIndex";
import { setCategorySnapshot } from "@/lib/categorySnapshot";
import { checkDevFailure, shouldFailId, SimulatedNetworkError } from "@/lib/devFailure";
import { computeFacets } from "@/lib/facets";
import {
  DEFAULT_BATCH_SIZE,
  chunkIds,
  mergeBatchResults,
  normalizeIds,
  type ChunkResult,
} from "@/lib/batchIds";
import { filterGenres, groupGenresByFloor, pickTopGenres, sortGenresForList } from "@/lib/genreMatch";
import { isReleased } from "@/lib/releaseDate";
import {
  getRankingWorks,
  getSuggestionsFromPool,
  getSuggestionTextFromPool,
  searchWorks as searchWorksSync,
} from "@/lib/search";
import { getGenreName, getGenresForAudience } from "@/lib/taxonomy";
import { getWorkRefKey } from "@/lib/workIdentity";
import { EMPTY_FILTERS, type AudienceType, type Work, type WorkFilters } from "@/types/work";
import type {
  CategoryCatalogResponse,
  FloorCatalogResponse,
  SearchableAttributesResponse,
  ServiceCatalogResponse,
} from "@/types/category";
import type { ContentTypeId, Floor, Genre } from "@/types/taxonomy";
import type {
  CatalogFacets,
  CatalogRepository,
  FacetOption,
  FacetRequest,
  GenreCatalogRequest,
  GenreCatalogResponse,
  NewWorksKind,
  NewWorksRequest,
  RankingRequest,
  RankingResponse,
  RecommendationRequest,
  RecommendationResponse,
  SearchCorrection,
  SearchRequest,
  SearchResponse,
  Suggestion,
  WorkListResponse,
  WorksByIdsResponse,
} from "@/types/catalog";

/**
 * デモデータ用リポジトリ。
 *
 * 本番では同じインターフェースを HTTP 実装に差し替える。
 * ここではデモデータに対して、検索・並び替え・ページング・ファセット集計を行う。
 * カテゴリ（商品形式・サービス・フロア・ジャンル・属性）も同じ契約で返し、
 * 取得した内容を表示用スナップショットへ反映する（＝UIは固定辞書を見ない）。
 */

const DEFAULT_PAGE_SIZE = 24;
/** ジャンル入口に出す「作品数の多いジャンル」の件数 */
const TOP_GENRES_LIMIT = 12;

/** ジャンル一覧の集計結果（limit/offset を除いた部分。条件ごとにキャッシュする） */
interface GenreCatalogBase {
  floors: Floor[];
  matched: Genre[];
  genreCounts: Record<string, number>;
  typeCounts: Record<string, number>;
  serviceCounts: Record<string, number>;
  floorCounts: Record<string, number>;
  makerOptions: FacetOption[];
  series: string[];
  totalWorks: number;
  modeGenreTotal: number;
  contentTypes: CategoryCatalogResponse["contentTypes"];
  services: CategoryCatalogResponse["services"];
}
const MAX_PAGE_SIZE = 100;

function normalizePageSize(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return DEFAULT_PAGE_SIZE;
  return Math.min(Math.floor(value), MAX_PAGE_SIZE);
}

function slicePage<T>(items: T[], page: number, pageSize: number): { items: T[]; totalPages: number; page: number } {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  return {
    items: items.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    page: currentPage,
    totalPages,
  };
}

function toOption(value: string, count: number, label = value, roles?: string[]): FacetOption {
  return roles ? { value, label, count, roles } : { value, label, count };
}

function makerOptions(mode: AudienceType, counts: Record<string, number>): FacetOption[] {
  return getMakers(mode)
    .map((maker) => toOption(maker, counts[maker] ?? 0))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja"));
}

function peopleOptions(mode: AudienceType, counts: Record<string, number>): FacetOption[] {
  return getPeople(mode)
    .map((person) => toOption(person.name, counts[person.name] ?? 0, person.name, person.roles))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja"));
}

function genreOptions(mode: AudienceType, counts: Record<string, number>): FacetOption[] {
  return getGenresForAudience(mode)
    .map((genre) => toOption(genre.id, counts[genre.id] ?? 0, getGenreName(genre.id)))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja"));
}

function buildFacets(filters: WorkFilters, mode: AudienceType): CatalogFacets {
  const counts = computeFacets(works, filters, mode);
  return {
    ...counts,
    makerOptions: makerOptions(mode, counts.makers),
    peopleOptions: peopleOptions(mode, counts.people),
  };
}

function emptyFilters(query: string): WorkFilters {
  return { ...EMPTY_FILTERS, q: query };
}

function time(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function daysSince(value: string, reference: Date): number {
  return (reference.getTime() - time(value)) / (1000 * 60 * 60 * 24);
}

function buildNewWorks(mode: AudienceType, kind: NewWorksKind): Work[] {
  const pool = getWorksByMode(mode);
  if (kind === "added") {
    return pool
      .filter((work) => {
        const diff = daysSince(work.addedAt, SITE_TODAY);
        return diff >= 0 && diff <= 7;
      })
      .slice()
      .sort((a, b) => time(b.addedAt) - time(a.addedAt) || a.id.localeCompare(b.id));
  }
  if (kind === "released") {
    return pool
      // 発売予定は「最近発売された作品」に含めない（詳細検索の「発売予定」で扱う）
      .filter((work) => isReleased(work.releaseDate, SITE_TODAY))
      .slice()
      .sort((a, b) => time(b.releaseDate) - time(a.releaseDate) || a.id.localeCompare(b.id));
  }
  return pool.slice().sort((a, b) => time(b.updatedAt) - time(a.updatedAt) || a.id.localeCompare(b.id));
}

/** カテゴリのマスタ（外部APIの /categories に相当するデモデータ） */
function buildCategoryPayload(): CategoryCatalogResponse {
  return {
    contentTypes: mockContentTypes.map((type) => ({
      ...type,
      labelSource: "local" as const,
      conditionSet: contentTypeConditionSets[type.id],
    })),
    services: mockServices,
    floors: mockFloors,
    genres: mockGenres,
    attributes: mockAttributes,
    source: "mock",
  };
}

export class MockCatalogRepository implements CatalogRepository {
  readonly kind = "mock" as const;

  private genreCache = new Map<string, GenreCatalogBase>();

  /** モード別カテゴリ（ジャンルをモードで絞ったもの） */
  private modeSnapshots = new Map<AudienceType, CategoryCatalogResponse>();

  private newWorksCache = new Map<string, Work[]>();

  /** 取得済み作品のキャッシュ（同じIDを何度も解決しない） */
  private workCache = new Map<string, Work>();

  private categoryCache: CategoryCatalogResponse | null = null;

  private readCategory(): CategoryCatalogResponse {
    if (!this.categoryCache) {
      this.categoryCache = buildCategoryPayload();
    }
    return this.categoryCache;
  }

  /** カテゴリ情報を取得し、表示用スナップショットにも反映する */
  async getContentTypes(): Promise<CategoryCatalogResponse> {
    const failure = checkDevFailure("genres");
    if (failure.active) throw new SimulatedNetworkError("genres");
    const payload = this.readCategory();
    setCategorySnapshot(payload);
    return payload;
  }

  async getServices(): Promise<ServiceCatalogResponse> {
    return { services: this.readCategory().services, source: "mock" };
  }

  async getFloors(serviceId?: string | null): Promise<FloorCatalogResponse> {
    const payload = this.readCategory();
    if (!serviceId) return { serviceId: null, floors: payload.floors, source: "mock" };
    const service = payload.services.find((item) => item.id === serviceId);
    const floors = service
      ? payload.floors.filter((floor) => service.floorIds.includes(floor.id))
      : payload.floors;
    return { serviceId, floors, source: "mock" };
  }

  async getSearchableAttributes(contentType: ContentTypeId | null): Promise<SearchableAttributesResponse> {
    const payload = this.readCategory();
    const conditionSet = contentType ? contentTypeConditionSets[contentType] ?? null : null;
    const order = conditionSet?.attributeIds ?? [];
    const allowed = (definitionId: string) => order.length === 0 || order.includes(definitionId);
    const attributes = payload.attributes
      .filter((definition) => {
        if (contentType === null) return true;
        if (order.length > 0) return allowed(definition.id);
        return definition.contentTypeIds.length === 0 || definition.contentTypeIds.includes(contentType);
      })
      .map((definition) => ({
        definition,
        contentTypeIds: definition.contentTypeIds,
        supportsRange: definition.valueType === "number",
        labelSource: conditionSet?.providerAttributeIds.includes(definition.id)
          ? ("provider" as const)
          : ("local" as const),
      }))
      .sort((a, b) => order.indexOf(a.definition.id) - order.indexOf(b.definition.id));

    return { contentType, conditionSet, attributes, source: "mock" };
  }

  async searchWorks(request: SearchRequest): Promise<SearchResponse> {
    const failure = checkDevFailure("search");
    if (failure.active) throw new SimulatedNetworkError("search");
    const pageSize = normalizePageSize(request.pageSize);
    const matched = searchWorksSync(request.filters, request.sort, request.mode);
    const paged = slicePage(matched, request.page, pageSize);

    return {
      items: paged.items,
      total: matched.length,
      page: paged.page,
      pageSize,
      totalPages: paged.totalPages,
      facets: buildFacets(request.filters, request.mode),
      appliedFilters: request.filters,
      mode: request.mode,
      source: "mock",
    };
  }

  async countWorks(query: string, mode: AudienceType, filters?: WorkFilters): Promise<number> {
    const failure = checkDevFailure("search");
    if (failure.active) throw new SimulatedNetworkError("search");
    const trimmed = query.trim();
    if (!trimmed) return 0;
    const base = filters ? { ...filters, q: trimmed } : emptyFilters(trimmed);
    return searchWorksSync(base, "relevance", mode).length;
  }

  private resolveOne(id: string): Work | null {
    const key = id.toLowerCase();
    const cached = this.workCache.get(key);
    if (cached) return cached;
    const work = getIdIndex().get(key) ?? getWorkByRefKey(id) ?? null;
    if (work) this.workCache.set(key, work);
    return work;
  }

  async getWorkById(id: string): Promise<Work | null> {
    const failure = checkDevFailure("detail");
    if (failure.active) throw new SimulatedNetworkError("detail");
    const value = id.trim();
    if (!value) return null;
    return this.resolveOne(value);
  }

  /** 1チャンク分のIDを解決する（取得できないIDは missingIds として返す） */
  private resolveChunk(chunk: string[]): ChunkResult<Work> {
    const items: Work[] = [];
    const missingIds: string[] = [];
    chunk.forEach((id) => {
      const work = this.resolveOne(id);
      if (work) items.push(work);
      else missingIds.push(id);
    });
    return { items, missingIds };
  }

  /**
   * 保存リストのIDをまとめて解決する。
   * 1リクエストの件数を区切り、重複を除き、依頼順を保って返す。
   * 取得できないIDは missingIds とし、他の作品まで消さない。
   */
  async getWorksByIds(ids: string[]): Promise<WorksByIdsResponse> {
    const unique = normalizeIds(ids);
    if (unique.length === 0)
      return { items: [], missingIds: [], unresolvedIds: [], partial: false, source: "mock" };

    // 開発時のみ：通信失敗を再現する（本番ビルドでは必ず active=false）
    const failure = checkDevFailure("saved");
    if (failure.all) throw new SimulatedNetworkError("saved");

    if (failure.partial) {
      // 本番の「チャンク単位の失敗」と同じ経路を通す。
      // 1件ずつのリクエストに分け、決定的に一部だけ失敗させる。
      const partialResults: (ChunkResult<Work> | null)[] = unique.map((_id, index) =>
        shouldFailId(index, unique.length) ? null : this.resolveChunk([unique[index]]),
      );
      const mergedPartial = mergeBatchResults(unique, partialResults, (work) => [work.id, getWorkRefKey(work)]);
      return {
        items: mergedPartial.items,
        missingIds: mergedPartial.missingIds,
        unresolvedIds: mergedPartial.unresolvedIds,
        partial: mergedPartial.failed,
        source: "mock",
      };
    }

    const chunks = chunkIds(unique, DEFAULT_BATCH_SIZE);
    const results: ChunkResult<Work>[] = chunks.map((chunk) => this.resolveChunk(chunk));

    const merged = mergeBatchResults(unique, results, (work) => [work.id, getWorkRefKey(work)]);
    return {
      items: merged.items,
      missingIds: merged.missingIds,
      unresolvedIds: merged.unresolvedIds,
      partial: false,
      source: "mock",
    };
  }

  async getSuggestions(query: string, mode: AudienceType, limit = 8): Promise<Suggestion[]> {
    const failure = checkDevFailure("search");
    if (failure.active) throw new SimulatedNetworkError("search");
    const pool = works.filter((work) => matchesAudience(work, mode));
    return getSuggestionsFromPool(query, mode, pool, limit);
  }

  async getSearchCorrection(query: string, mode: AudienceType): Promise<SearchCorrection | null> {
    const failure = checkDevFailure("search");
    if (failure.active) throw new SimulatedNetworkError("search");
    const pool = works.filter((work) => matchesAudience(work, mode));
    return getSuggestionTextFromPool(query, mode, pool);
  }

  async getGenreFacets(request: FacetRequest): Promise<FacetOption[]> {
    const counts = computeFacets(works, request.filters, request.mode);
    const options = genreOptions(request.mode, counts.genres);
    return typeof request.limit === "number" ? options.slice(0, request.limit) : options;
  }

  async getPeopleFacets(request: FacetRequest): Promise<FacetOption[]> {
    const counts = computeFacets(works, request.filters, request.mode);
    const options = peopleOptions(request.mode, counts.people);
    return typeof request.limit === "number" ? options.slice(0, request.limit) : options;
  }

  async getMakerFacets(request: FacetRequest): Promise<FacetOption[]> {
    const counts = computeFacets(works, request.filters, request.mode);
    const options = makerOptions(request.mode, counts.makers);
    return typeof request.limit === "number" ? options.slice(0, request.limit) : options;
  }

  /** モード別のカテゴリ情報。ジャンルは audienceIds で絞るため、モード間で混ざらない。 */
  private readCategoryForMode(mode: AudienceType): CategoryCatalogResponse {
    const cached = this.modeSnapshots.get(mode);
    if (cached) return cached;
    const base = this.readCategory();
    const payload: CategoryCatalogResponse = {
      ...base,
      genres: base.genres.filter(
        (genre) => genre.audienceIds.includes(mode) || genre.audienceIds.includes("common"),
      ),
    };
    this.modeSnapshots.set(mode, payload);
    return payload;
  }

  /** ジャンル一覧の集計部分（条件が同じなら再計算しない） */
  private buildGenreBase(request: GenreCatalogRequest, category: CategoryCatalogResponse): GenreCatalogBase {
    const modeAll = getWorksByMode(request.mode);
    const modeWorks = request.contentType
      ? modeAll.filter((work) => work.contentType === request.contentType)
      : modeAll;

    const genreCounts: Record<string, number> = {};
    const typeCounts: Record<string, number> = {};
    const makerCounts: Record<string, number> = {};
    const seriesSet = new Set<string>();

    modeAll.forEach((work) => {
      typeCounts[work.contentType] = (typeCounts[work.contentType] ?? 0) + 1;
    });
    modeWorks.forEach((work) => {
      makerCounts[work.maker] = (makerCounts[work.maker] ?? 0) + 1;
      if (work.series) seriesSet.add(work.series);
      work.genreIds.forEach((id) => {
        genreCounts[id] = (genreCounts[id] ?? 0) + 1;
      });
    });

    // サービス → フロアの関係はサービスが持つ floorIds だけで判定する
    // （名前の前方一致などで階層を推測しない）。存在しないサービスなら 0 件。
    const service = request.serviceId
      ? category.services.find((item) => item.id === request.serviceId) ?? null
      : null;
    const serviceFloorIds = request.serviceId ? new Set(service ? service.floorIds : []) : null;

    const floors = category.floors
      .filter((floor) => !serviceFloorIds || serviceFloorIds.has(floor.id))
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const scoped = filterGenres(category.genres, {
      contentType: request.contentType ?? null,
      floorIds: serviceFloorIds ? floors.map((floor) => floor.id) : null,
      query: request.q ?? "",
    });

    const floorCounts: Record<string, number> = {};
    scoped.forEach((genre) => {
      floorCounts[genre.floorId] = (floorCounts[genre.floorId] ?? 0) + 1;
    });

    const serviceCounts: Record<string, number> = {};
    category.services.forEach((item) => {
      serviceCounts[item.id] = scoped.filter((genre) => item.floorIds.includes(genre.floorId)).length;
    });

    const matched = request.floorId
      ? scoped.filter((genre) => genre.floorId === request.floorId)
      : scoped;

    return {
      floors,
      matched: sortGenresForList(matched, floors, genreCounts),
      genreCounts,
      typeCounts,
      serviceCounts,
      floorCounts,
      makerOptions: getMakers(request.mode)
        .map((maker) => toOption(maker, makerCounts[maker] ?? 0))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "ja")),
      series: Array.from(seriesSet).sort((a, b) => a.localeCompare(b, "ja")),
      totalWorks: modeWorks.length,
      modeGenreTotal: category.genres.length,
      contentTypes: category.contentTypes,
      services: category.services,
    };
  }

  /**
   * ジャンル一覧・件数。
   * 条件（モード・形式・サービス・フロア・検索語）ごとに集計をキャッシュし、
   * limit/offset だけを都度適用する。全ジャンルを一度に描画しないための上限もここで扱う。
   */
  async getGenres(request: GenreCatalogRequest): Promise<GenreCatalogResponse> {
    const failure = checkDevFailure("genres");
    if (failure.active) throw new SimulatedNetworkError("genres");
    const category = this.readCategoryForMode(request.mode);
    // 表示名の解決をモード別に切り替える（反対モードのジャンル名で誤表示しない）
    setCategorySnapshot(category, request.mode);

    const query = (request.q ?? "").trim();
    const cacheKey = [
      request.mode,
      request.contentType ?? "",
      request.serviceId ?? "",
      request.floorId ?? "",
      query,
    ].join("|");

    let base = this.genreCache.get(cacheKey);
    if (!base) {
      base = this.buildGenreBase({ ...request, q: query }, category);
      this.genreCache.set(cacheKey, base);
    }

    const offset = Math.max(0, request.offset ?? 0);
    const limit = typeof request.limit === "number" ? Math.max(0, request.limit) : base.matched.length;
    const visible = limit === 0 ? [] : base.matched.slice(offset, offset + limit);

    return {
      mode: request.mode,
      floors: base.floors.filter((floor) => (base.floorCounts[floor.id] ?? 0) > 0),
      genres: visible,
      groups: groupGenresByFloor(base.floors, visible),
      genreCounts: base.genreCounts,
      typeCounts: base.typeCounts,
      serviceCounts: base.serviceCounts,
      floorCounts: base.floorCounts,
      totalGenres: base.matched.length,
      topGenres: pickTopGenres(base.matched, base.genreCounts, TOP_GENRES_LIMIT),
      modeGenreTotal: base.modeGenreTotal,
      contentTypes: base.contentTypes,
      services: base.services,
      makerOptions:
        typeof request.makerLimit === "number"
          ? base.makerOptions.slice(0, request.makerLimit)
          : base.makerOptions,
      series:
        typeof request.seriesLimit === "number" ? base.series.slice(0, request.seriesLimit) : base.series,
      totalWorks: base.totalWorks,
      source: "mock",
    };
  }

  async getNewWorks(request: NewWorksRequest): Promise<WorkListResponse> {
    const pageSize = normalizePageSize(request.pageSize);
    const cacheKey = `${request.mode}|${request.kind}`;
    let pool = this.newWorksCache.get(cacheKey);
    if (!pool) {
      pool = buildNewWorks(request.mode, request.kind);
      this.newWorksCache.set(cacheKey, pool);
    }
    const paged = slicePage(pool, request.page, pageSize);
    return {
      items: paged.items,
      total: pool.length,
      page: paged.page,
      pageSize,
      totalPages: paged.totalPages,
      kind: request.kind,
      source: "mock",
    };
  }

  async getRecommendationCandidates(request: RecommendationRequest): Promise<RecommendationResponse> {
    const matched = searchWorksSync(request.filters, request.sort, request.mode);
    return {
      items: matched.slice(0, Math.max(0, request.limit)),
      total: matched.length,
      mode: request.mode,
      source: "mock",
    };
  }

  async getRanking(request: RankingRequest): Promise<RankingResponse> {
    const pageSize = normalizePageSize(request.pageSize);
    const ranked = getRankingWorks(request.mode, request.type);
    const paged = slicePage(ranked, request.page, pageSize);
    return {
      items: paged.items,
      total: ranked.length,
      page: paged.page,
      pageSize,
      totalPages: paged.totalPages,
      type: request.type,
    };
  }

  async getRelatedWorks(id: string, limit = 4): Promise<Work[]> {
    const failure = checkDevFailure("detail");
    if (failure.active) throw new SimulatedNetworkError("detail");
    const source = this.resolveOne(id);
    if (!source) return [];
    return getRelatedWorksSync(source, limit);
  }
}

export const mockCatalogRepository = new MockCatalogRepository();